/*
  NS Editorial gallery: step carousel behaviour.

  Deliberately a near-copy of assets/ns-full-bleed-carousel.js, with the selectors renamed
  to this section's own data attributes. It is NOT a shared file: that module binds to
  [data-ns-fbc] / [data-ns-fbc-slide] / [data-ns-fbc-dot], which are named for that section
  and cannot be renamed there without touching a file another section depends on. Rather
  than have two sections share one carousel-named module, this mirrors its structure,
  naming and lifecycle so both behave identically and either can be deleted alone.

  Differences from that module, each driven by this design:

  - TWO controls over one slide set, like the full-bleed's label row: a thumbnail rail
    (thumb + uppercase label) and a dot row. Both are real <button>s and both stay in step
    whichever one was used.
  - Each step is THREE elements that move together: the main image, the rail row and the
    body copy. Desktop shows one main image with the rail beside it; tablet and mobile lay
    the same three parts out as cards in a scrolling track. goTo() therefore switches a
    group per index rather than a single slide.
  - No autoplay. The design gives this section dots and a labelled rail but no timing, and
    an autoplaying step sequence would fight a reader part-way through the body copy.

  Below 990 the track scrolls natively and the cards are all visible at once, so the active
  step is a scroll position rather than a visibility toggle. The JS keeps the controls in
  step with the scroll there instead of hiding anything: hiding a card the visitor can
  still scroll to would strand them on an empty track.

  Only loaded when the section has 2+ step blocks; a single step is static CSS with no JS
  at all. Step count is content-driven: whatever the merchant adds in the theme editor is
  what renders. Nothing here assumes a fixed number.
*/
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  /* Matches the stylesheet's breakpoint. Above it the steps cross-fade in place; below it
     they are a scrolling track, and the two need different behaviour from the same DOM. */
  var TRACK_QUERY = window.matchMedia('(max-width: 989px)');

  function NsEditorialGallery(root) {
    this.root = root;
    this.track = root.querySelector('[data-ns-eg-track]');
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-ns-eg-card]'));
    this.mains = Array.prototype.slice.call(root.querySelectorAll('[data-ns-eg-main]'));
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-ns-eg-tab]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-ns-eg-dot]'));
    this.copies = Array.prototype.slice.call(root.querySelectorAll('[data-ns-eg-copy]'));
    this.index = 0;
    this.scrollTimer = null;
    this.releaseTimer = null;
    this.programmaticScroll = false;

    if (this.cards.length < 2) return;

    this.bind();
    this.playActiveVideo();
  }

  NsEditorialGallery.prototype.bind = function () {
    var self = this;

    // Two controls over one step set, so they bind identically.
    this.tabs.concat(this.dots).forEach(function (control) {
      control.addEventListener('click', function () {
        self.goTo(parseInt(control.dataset.index, 10), true);
      });
    });

    /* Arrow keys move between steps. Bound on the root rather than each button so the
       whole component responds once the visitor has focus anywhere inside it. */
    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        self.goTo((self.index + 1) % self.cards.length, true);
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        self.goTo((self.index - 1 + self.cards.length) % self.cards.length, true);
      }
    });

    /* Below 990 the visitor can scroll the track directly, so the controls have to follow
       the scroll or they would report the wrong step. Debounced: scroll fires far more
       often than the active step actually changes. */
    if (this.track) {
      this.track.addEventListener('scroll', function () {
        if (!TRACK_QUERY.matches) return;
        /* Ignore the scroll our own goTo() started. The trailing cards share a clamped
           target, so re-deriving the index from that position would undo the explicit
           choice the visitor just made with a dot or a thumbnail. */
        if (self.programmaticScroll) return;
        clearTimeout(self.scrollTimer);
        self.scrollTimer = setTimeout(function () { self.syncToScroll(); }, 80);
      }, { passive: true });
    }
  };

  /* Marks the control at `to` current and the one at `from` not.
     Guarded on existence: the dot row and the rail are built from the same block set but a
     step may carry no label, so the two lists are not guaranteed to be the same length.

     aria-current, not aria-selected: these are buttons that move a carousel, not tabs in a
     tablist, and there is no tabpanel wiring to justify the tab role here. */
  function syncControls(controls, from, to, token) {
    if (controls[from]) {
      controls[from].classList.remove('is-active');
      controls[from].removeAttribute('aria-current');
    }
    if (controls[to]) {
      controls[to].classList.add('is-active');
      controls[to].setAttribute('aria-current', token);
    }
  }

  /* Panels carry aria-hidden when out of view, so a screen reader is offered the visible
     step only rather than all three sets of copy at once.

     Only applied above the track breakpoint. Below it every card is on screen and reachable
     by scrolling, so hiding them from assistive tech would misdescribe the page. */
  function syncPanels(panels, from, to, hide) {
    if (panels[from]) {
      panels[from].classList.remove('is-active');
      if (hide) panels[from].setAttribute('aria-hidden', 'true');
      else panels[from].removeAttribute('aria-hidden');
    }
    if (panels[to]) {
      panels[to].classList.add('is-active');
      panels[to].removeAttribute('aria-hidden');
    }
  }

  /* The scroll offset that puts card `i` at the track's resting position.

     🔴 The leading padding has to come off. The track carries a padding-inline that
     restores the design's 24/16 side inset, and offsetLeft is measured from the PADDING
     edge, so card 0 reports offsetLeft = 24. Scrolling to 24 would park it flush against
     the viewport edge, which is exactly the inset this padding exists to create. */
  NsEditorialGallery.prototype.offsetFor = function (i) {
    var pad = parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
    var target = this.cards[i].offsetLeft - this.track.offsetLeft - pad;

    /* Clamp to what the scroller can actually reach. On a peeking track more than one card
       is visible at once, so the last card has too little content to its right to ever
       reach the leading inset: the scroller runs out first. Asking for an unreachable
       offset would leave syncToScroll comparing against a position the track can never
       hold, and the active step would stick on the second-to-last card. Scrolling to the
       end is the correct resting place for the final step. */
    var max = this.track.scrollWidth - this.track.clientWidth;
    return Math.max(0, Math.min(target, max));
  };

  /* Works out which card is nearest the track's resting position and marks it current,
     without scrolling anything: this runs in response to the visitor's own scrolling. */
  NsEditorialGallery.prototype.syncToScroll = function () {
    var left = this.track.scrollLeft;
    var nearest = 0;
    var best = Infinity;

    /* `<=` not `<`, so a tie resolves to the HIGHER index. On a peeking track the trailing
       cards share a clamped target (they all bottom out at the scroller's maximum), and a
       `<` comparison would keep the first of them and leave the rail stuck one step short
       of the end. The visitor scrolled right, so the later card is the one they meant. */
    for (var i = 0; i < this.cards.length; i++) {
      var delta = Math.abs(this.offsetFor(i) - left);
      if (delta <= best) { best = delta; nearest = i; }
    }

    if (nearest !== this.index) this.setActive(nearest);
  };

  NsEditorialGallery.prototype.pauseVideos = function () {
    this.mains.forEach(function (panel) {
      var video = panel.querySelector('video');
      if (video) video.pause();
    });
  };

  NsEditorialGallery.prototype.playActiveVideo = function () {
    this.pauseVideos();
    if (REDUCED_MOTION.matches || !this.mains[this.index]) return;

    var video = this.mains[this.index].querySelector('video');
    if (!video) return;

    var played = video.play();
    if (played && typeof played.catch === 'function') played.catch(function () {});
  };

  /* State only: updates the controls and panels without moving the track. */
  NsEditorialGallery.prototype.setActive = function (i) {
    var from = this.index;
    this.index = i;

    var hide = !TRACK_QUERY.matches;
    syncPanels(this.mains, from, i, hide);
    syncPanels(this.copies, from, i, hide);
    syncPanels(this.cards, from, i, false);
    /* Each control keeps the aria-current token the markup gave it: the rail names steps,
       the dot row names positions. */
    syncControls(this.tabs, from, i, 'step');
    syncControls(this.dots, from, i, 'true');
    this.playActiveVideo();
  };

  NsEditorialGallery.prototype.goTo = function (i, scroll) {
    if (i === this.index || i < 0 || i >= this.cards.length) return;

    this.setActive(i);

    /* Below 990 the steps are a real scrolling track, so moving between them means moving
       the scroll position. Above it the cards are stacked and the class switch is the whole
       transition, so there is nothing to scroll. */
    if (scroll && this.track && TRACK_QUERY.matches) {
      var self = this;
      this.programmaticScroll = true;
      clearTimeout(this.releaseTimer);
      /* Released once the smooth scroll has settled; the flag only has to outlive the
         scroll events this call generates. */
      this.releaseTimer = setTimeout(function () {
        self.programmaticScroll = false;
      }, REDUCED_MOTION.matches ? 50 : 700);

      this.track.scrollTo({
        left: this.offsetFor(i),
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth'
      });
    }
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-ns-eg]').forEach(function (el) {
      if (!el.nsEditorialGallery) el.nsEditorialGallery = new NsEditorialGallery(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Re-initialise when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
